import moment from "moment-timezone";

import { STATION_TIME_ZONE } from "./stationTime";
import type { TenantConfig, TenantConfigService } from "../types/tenantConfig";

/**
 * The cancellation rule (M10; completion plan §0.3 D5) — the END-USER copy,
 * which only EXPLAINS: the "Stornierung" block before a booking is confirmed
 * (`CancellationTermsBlock`). Whether a booking may be self-cancelled is the
 * server's verdict on `/me/bookings` (`bookerCancelVerdict`,
 * new-backend/app), which this app renders and never re-derives (ADR-0020).
 *
 * Copies, pinned by `shared/cancellation/cancellation-rule-vectors.json`:
 * new-backend/admin/src/lib/booking/cancellationRule.ts (validates the save),
 * new-backend/app/src/lib/booking/cancellationRule.ts (the verdict), and
 * this file (deadline, quote and fee only — the rule arrives already
 * validated by the server's projection, so no validator is copied here).
 *
 * Semantics: free until the LARGEST `daysBefore`; from each `daysBefore` its
 * percent applies. A deadline is the start minus N calendar days at the same
 * wall-clock time in the location's zone — moment's `subtract(N, "days")` on
 * the location's clock.
 */

export interface CancellationTier {
  daysBefore: number;
  percent: number;
}

export interface CancellationRule {
  tiers: CancellationTier[];
  agbConfirmed: true;
}

/** A category's explicit "no rule here; the AGB governs". */
export const CANCELLATION_NONE = "none";
/**
 * A category whose stored rule the server could not read (a hand edit). The
 * app shows the AGB sentence and never falls back to the use case's rule; the
 * server refuses the self-cancel (`rule_unreadable`). Mirrors
 * `CANCELLATION_UNREADABLE` in new-backend/app `tenantConfig.ts`.
 */
export const CANCELLATION_UNREADABLE = "unreadable";
export type CancellationSetting =
  | CancellationRule
  | typeof CANCELLATION_NONE
  | typeof CANCELLATION_UNREADABLE;

/** A deadline: `daysBefore` calendar days before the start, same wall clock, in `timeZone`. */
export const cancellationDeadline = (
  start: moment.MomentInput,
  daysBefore: number,
  timeZone: string = STATION_TIME_ZONE
): moment.Moment => moment.tz(start, timeZone).subtract(daysBefore, "days");

export interface CancellationQuote {
  /** Free before this instant; the start itself when the rule has no tiers. */
  freeUntil: moment.Moment;
  /** The percent that applies at `now`; 0 while free. */
  feePercent: number;
  /** Each tier with the instant it applies from, furthest first. */
  tiers: Array<CancellationTier & { from: moment.Moment }>;
}

export const cancellationQuote = (
  rule: CancellationRule,
  start: moment.MomentInput,
  now: moment.MomentInput,
  timeZone: string = STATION_TIME_ZONE
): CancellationQuote => {
  const tiers = [...rule.tiers]
    .sort((a, b) => b.daysBefore - a.daysBefore)
    .map((tier) => ({ ...tier, from: cancellationDeadline(start, tier.daysBefore, timeZone) }));
  const at = moment(now).valueOf();
  const inForce = tiers.filter((tier) => tier.from.valueOf() <= at).pop();
  return {
    freeUntil: tiers[0]?.from ?? moment.tz(start, timeZone),
    feePercent: inForce?.percent ?? 0,
    tiers,
  };
};

/**
 * Until when a booking of this category is only an OFFER — nothing paid, no
 * contract — and so withdrawn free of charge (PO, 2026-10-19 on #3394). The
 * server decides the same on the stored order (`bookerCancelVerdict`:
 * no `contract_concluded_at`, nothing paid → `request`); this explains it
 * before the booking exists, from the configuration:
 *   - a direct booking is paid in the flow: no offer stage;
 *   - postpay (`paymentTiming: after`): until acceptance;
 *   - otherwise (pay-first, or unconfigured): until payment.
 */
export const offerStageFor = (
  config: TenantConfig | undefined,
  useCase: string,
  categoryKey: string | undefined,
  isDirectBooking: boolean
): "payment" | "acceptance" | null => {
  if (isDirectBooking) return null;
  const category = categoryKey
    ? config?.categories?.find((c) => c.key === categoryKey)
    : undefined;
  const timing =
    category?.paymentTiming ??
    config?.paymentTiming?.[useCase.toLowerCase() as TenantConfigService];
  return timing === "after" ? "acceptance" : "payment";
};

/** `percent` of `price`, rounded half up to the cent. */
export const cancellationFee = (price: number, percent: number): number =>
  Math.round((Math.round(price * 100) * percent) / 100) / 100;
